import React, { useEffect, useState } from "react";
import { fechaHoraLarga } from "@/lib/zona";
import { useNavigate } from "react-router-dom";
import { Mail, ExternalLink, XCircle, AlertTriangle, ChevronRight, ChevronLeft, Inbox } from "lucide-react";
import { useData } from "@/context/DataContext";

const ESTADO_META = {
  enviado_a_mi: { label: "Enviarme una copia", Icon: Mail, badge: "bg-[#DCFCE7] text-[#15803D]", dot: "#16A34A" },
  abierto_en_mi_correo: { label: "Abrir en mi correo", Icon: ExternalLink, badge: "bg-[#DBEAFE] text-[#1D4ED8]", dot: "#2563EB" },
  cancelado: { label: "Cancelado", Icon: XCircle, badge: "bg-[#E5E7EB] text-[#4B5563]", dot: "#9CA3AF" },
  error: { label: "Error", Icon: AlertTriangle, badge: "bg-[#FEE2E2] text-[#B91C1C]", dot: "#DC2626" },
};

const FILTROS = [
  { id: "all", label: "Todos" },
  { id: "enviado_a_mi", label: "Copia a mí" },
  { id: "abierto_en_mi_correo", label: "Abierto" },
  { id: "cancelado", label: "Cancelados" },
  { id: "error", label: "Errores" },
];

const fmtFecha = (iso) => {
  try {
    return fechaHoraLarga(iso);
  } catch { return iso; }
};

export default function Correos() {
  const navigate = useNavigate();
  const { listarCorreos } = useData();
  const [items, setItems] = useState(null);
  const [filtro, setFiltro] = useState("all");
  const [abierto, setAbierto] = useState(null);

  useEffect(() => {
    listarCorreos().then(setItems).catch(() => setItems([]));
    // eslint-disable-next-line
  }, []);

  const visibles = (items || []).filter((i) => filtro === "all" || i.estado === filtro);

  return (
    <div className="space-y-5">
      <header className="aldia-fade-up flex items-center gap-3">
        <button data-testid="correos-back" onClick={() => navigate("/perfil")}
          className="shrink-0 w-10 h-10 rounded-full bg-white border border-[#E5DFD5] flex items-center justify-center hover:border-[#3A6B56]">
          <ChevronLeft size={22} className="text-[#3A6B56]" />
        </button>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Historial de correos</h1>
          <p className="text-base text-[#6E7A74] mt-0.5">Los correos que has preparado y su estado.</p>
        </div>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="correos-filtros">
        {FILTROS.map((f) => (
          <button
            key={f.id}
            data-testid={`correos-filtro-${f.id}`}
            onClick={() => setFiltro(f.id)}
            className={`min-h-[40px] px-4 rounded-full text-sm font-semibold border transition-colors ${
              filtro === f.id ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5] hover:border-[#3A6B56]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="py-16 flex justify-center"><div className="w-10 h-10 rounded-full border-4 border-[#E5DFD5] border-t-[#3A6B56] animate-spin" /></div>
      ) : visibles.length === 0 ? (
        <div className="py-16 flex flex-col items-center gap-3 text-center" data-testid="correos-vacio">
          <Inbox size={40} className="text-[#C4BDB0]" />
          <p className="text-base text-[#6E7A74]">Aquí verás los correos que prepares desde el Agente.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="correos-list">
          {visibles.map((c) => {
            const meta = ESTADO_META[c.estado] || ESTADO_META.error;
            const Icon = meta.Icon;
            const open = abierto === c.id;
            return (
              <div key={c.id} data-testid={`correo-item-${c.id}`}
                className="bg-white border border-[#E5DFD5] rounded-2xl shadow-sm overflow-hidden">
                <button
                  data-testid={`correo-toggle-${c.id}`}
                  onClick={() => setAbierto(open ? null : c.id)}
                  className="w-full text-left p-4 flex items-center gap-3 hover:bg-[#FAF8F5] transition-colors"
                >
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
                    <Icon size={20} className="text-[#3A6B56]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block text-xs font-bold uppercase px-2 py-0.5 rounded-full ${meta.badge}`}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="font-heading font-bold text-base text-[#1C2421] mt-1 truncate">
                      {c.asunto_titulo || "Correo sin asunto vinculado"}
                    </p>
                    <p className="text-sm text-[#6E7A74] truncate">Para: {c.destinatario_final || "—"}</p>
                  </div>
                  <ChevronRight size={20} className={`text-[#C4BDB0] shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                </button>
                {open && (
                  <div className="px-4 pb-4 pt-1 border-t border-[#F2EDE4] space-y-1.5" data-testid={`correo-detalle-${c.id}`}>
                    <Detalle k="Fecha" v={fmtFecha(c.created_at)} />
                    <Detalle k="Asunto relacionado" v={c.asunto_titulo || "Sin asunto vinculado"} />
                    <Detalle k="Destinatario final" v={c.destinatario_final || "—"} />
                    <Detalle k="Acción realizada" v={meta.label} />
                    {c.message_id && <Detalle k="Id del mensaje" v={c.message_id} />}
                    {c.asunto_id && (
                      <button
                        data-testid={`correo-ir-asunto-${c.id}`}
                        onClick={() => navigate(`/asunto/${c.asunto_id}`)}
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3A6B56] hover:underline"
                      >
                        Ver el asunto relacionado <ChevronRight size={15} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Detalle = ({ k, v }) => (
  <p className="text-sm text-[#4A5550] break-words">
    <span className="text-[#6E7A74]">{k}:</span> <span className="text-[#1C2421]">{v}</span>
  </p>
);
